import React, { useLayoutEffect, useRef } from 'react';
import { Bullseye, LifePreserver, CupHot } from 'react-bootstrap-icons'; // Ensure you have lucide-react installed

const IntentSelection = ({ onSelect }) => {

  const topRef = useRef(null);

  useLayoutEffect(() => {

    // 1. Try standard window scroll first
    window.scrollTo(0, 0);
    if (topRef.current) {
      let parent = topRef.current.parentElement;
      
      while (parent) {
        const style = window.getComputedStyle(parent);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
          parent.scrollTop = 0;
          break;
        }
        parent = parent.parentElement;
      }

      topRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }, []);
  
  const options = [
    {
      id: 'challenge',
      title: 'Seek Advice for Challenges',
      icon: <LifePreserver size={32} color="#e53e3e" />, // Red for urgency
      description: 'I am facing a specific obstacle or conflict and need a solution.',
      // Hidden prompt sent to AI
      backendPrompt: "CONTEXT: The user has selected 'Seek Advice for Challenges'. They have a specific problem. Act as your defined persona and greet them briefly in spoken Burmese (Myanmar Language). Ask them to describe **their current role or situation and the specific problem** they are facing."
    },
    {
      id: 'goal',
      title: 'Improve Your Goal',
      icon: <Bullseye size={32} color="#38a169" />, // Green for growth
      description: 'I want to create a roadmap or improve a specific skill.',
      // Hidden prompt sent to AI
      backendPrompt: "CONTEXT: The user has selected 'Improve a Goal'. They want to grow. Act as your defined persona and greet them enthusiastically in spoken Burmese (Myanmar Language). Ask them to describe **what they are currently doing (job or study) and what specific level** they want to reach."
    },
    {
      id: 'explore',
      title: 'Explore & Discuss',
      icon: <CupHot size={32} color="#3182ce" />, // Blue for casual
      description: 'I want to discuss relevant ideas, learn something new, or just chat.',
      // Hidden prompt sent to AI
      backendPrompt: "CONTEXT: The user has selected 'Explore & Discuss'. They want a casual or intellectual conversation. Act as your defined persona and greet them in spoken Burmese (Myanmar Language). Ask **what topic or field** they want to discuss today."
    }
  ];

  return (
    <div ref={topRef} className="intent-selection-container fade-in" style={{ textAlign: 'center', padding: '40px 20px' }}>
      <h4 style={{ marginBottom: '10px', color: '#444', fontWeight: '600' }}>How can I help you today?</h4>
      <p style={{ marginBottom: '30px', color: '#666' }}>Select an option to start the conversation.</p>
      
      <div className="intent-grid" style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '20px', 
        maxWidth: '900px', 
        margin: '0 auto' 
      }}>
        {options.map((opt) => (
          <button
            key={opt.id}
            onClick={() => onSelect(opt.backendPrompt)} 
            className="intent-card"
            style={{
              padding: '20px',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              background: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '15px',
              textAlign: 'center'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
              e.currentTarget.style.borderColor = '#b794f4'; 
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = '#e2e8f0';
            }}
          >
            <div style={{ padding: '15px', background: '#f7fafc', borderRadius: '50%' }}>
              {opt.icon}
            </div>
            <div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '8px', color: '#2d3748' }}>{opt.title}</h3>
              <p style={{ fontSize: '0.9rem', color: '#718096', lineHeight: '1.5' }}>{opt.description}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default IntentSelection;